import React, { useCallback, useState } from 'react';
import { Typography, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import { Grey2, trakrefBlue } from '../../../color_utils';
import SystemGrid from './SystemGrid';
import '../../../components/Common/Table/Table.css';

const MaterialTypeGrid = ({ materialTypes, locationName }) => {
    const [expandedRowIds, setExpandedRowIds] = useState([]);

    // Custom toggle with arrow icon (same as WorkOrders)
    const CustomDetailPanelToggle = useCallback(({ row }) => {
        const isOpen = expandedRowIds.includes(row.id);
        return (
            <Tooltip title={isOpen ? "Hide Systems" : "View Systems"}>
                <IconButton size="medium" tabIndex={-1}>
                    <ExpandMoreIcon
                        sx={(theme) => ({
                            color: Grey2,
                            transform: `rotateZ(${isOpen ? 180 : 0}deg)`,
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.complex
                            }),
                        })}
                        fontSize="inherit"
                    />
                </IconButton>
            </Tooltip>
        );
    }, [expandedRowIds]);

    const columns = [
        {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            renderCell: ({ row }) => {
                if (row.systems && row.systems.length > 0) {
                    return <CustomDetailPanelToggle row={row} />;
                }
                return null;
            },
        },
        {
            field: 'materialTypeName',
            headerName: 'Material Type',
            flex: 2,
        },
        {
            field: 'startWeightLbs',
            headerName: 'Start Weight (lbs)',
            type: 'number',
            valueFormatter: (value) => value?.toLocaleString() || '0',
        },
        {
            field: 'startWeightMt',
            headerName: 'Start Weight (Mt)',
            type: 'number',
            valueFormatter: (value) => value?.toFixed(4) || '0.0000',
        },
        {
            field: 'endWeightLbs',
            headerName: 'End Weight (lbs)',
            type: 'number',
            valueFormatter: (value) => value?.toLocaleString() || '0',
        },
        {
            field: 'endWeightMt',
            headerName: 'End Weight (Mt)',
            type: 'number',
            valueFormatter: (value) => value?.toFixed(4) || '0.0000',
        },
        {
            field: 'differenceLbs',
            headerName: 'Difference (lbs)',
            type: 'number',
            valueFormatter: (value) => value?.toLocaleString() || '0',
        },
        {
            field: 'differenceMt',
            headerName: 'Difference (Mt)',
            type: 'number',
            valueFormatter: (value) => value?.toFixed(4) || '0.0000',
        },
        {
            field: 'potentialEmissions',
            headerName: 'Potential Emissions (MtCO₂e)',
            type: 'number',
            valueFormatter: (value) => value?.toFixed(4) || '0.0000',
        },
    ];

    const getDetailPanelContent = useCallback(({ row }) => {
        return <SystemGrid systems={row.systems} materialTypeName={row.materialTypeName} />;
    }, []);

    if (!materialTypes || materialTypes.length === 0) {
        return null;
    }

    return (
        <div className="subtable">
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: trakrefBlue, mb: 1 }}>
                {locationName} — by Material Type
            </Typography>
            <DataGridPro
                rows={materialTypes}
                columns={columns}
                hideFooter
                disableRowSelectionOnClick
                columnHeaderHeight={45}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={() => 'auto'}
                detailPanelExpandedRowIds={expandedRowIds}
                onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
                getRowClassName={params => params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'}
                slotProps={{
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                }}
                sx={{
                    '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
                        flex: 1,
                    },
                }}
            />
        </div>
    );
};

export default MaterialTypeGrid;
